// Documents — READ-ONLY generated invoices/receipts.
//
// go-pim generates documents at settle time; the panel lists + links to them.
// There is NO "list all documents for a company" endpoint yet — the backend
// only lists by payment_ref — so this page is a LOOKUP by payment reference
// (with an optional company filter). A company-wide feed is a future backend
// addition; when it lands, add a second query path here.
//
// Each row links to its public shareable `/r/{slug}` view (go-pim's public
// origin, NOT the SPA) — always opened in a new tab with rel="noopener".
import { useState, type FormEvent } from "react";

import { I } from "@/components/Icons";
import { Pill, StatusPill } from "@/components/UI";
import { fmtCcyMinor, formatDateTime } from "@/utils/format";
import { useCompanies } from "@/queries/useCompanies";
import { useDocuments } from "@/queries/useDocuments";
import { useUI } from "@/store/ui";
import type { ListDocumentsParams } from "@/api/documents";

export function Documents() {
  const addToast = useUI((s) => s.addToast);
  const { data: companies = [] } = useCompanies();

  // Draft inputs (typed now) vs the submitted query (drives the fetch).
  const [paymentRef, setPaymentRef] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [query, setQuery] = useState<ListDocumentsParams | null>(null);

  const { data: documents = [], isLoading, isError, isFetched } =
    useDocuments(query);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const ref = paymentRef.trim();
    if (ref === "") return;
    setQuery({
      payment_ref: ref,
      company_id: companyId || undefined,
    });
  }

  function copyLink(url: string) {
    void navigator.clipboard?.writeText(url).then(
      () => addToast({ title: "Link copied", variant: "success" }),
      () => addToast({ title: "Couldn't copy link", variant: "error" }),
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · go-pim</div>
          <h1>Documents</h1>
          <div className="sub">
            Look up generated receipts &amp; invoices by payment reference.
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <form onSubmit={handleSubmit}>
          <div className="row gap-12" style={{ alignItems: "flex-end" }}>
            <div className="field" style={{ flex: 1 }}>
              <label className="label" htmlFor="doc-ref">
                Payment reference
              </label>
              <input
                id="doc-ref"
                className="input"
                type="text"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="e.g. pay_ABC123 / order reference"
                autoFocus
              />
            </div>
            <div className="field" style={{ width: 220 }}>
              <label className="label" htmlFor="doc-company">
                Company (optional)
              </label>
              <select
                id="doc-company"
                className="input"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">All companies</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn accent"
              type="submit"
              disabled={paymentRef.trim() === ""}
            >
              <I.Search size={12} /> Look up
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: "var(--card-gap, 16px)" }}>
        {query == null ? (
          <div className="empty">
            <div className="icon">
              <I.ClipboardCheck size={16} />
            </div>
            <h3>Search by payment reference</h3>
            <p>
              Enter a payment reference above to list the documents generated for
              that payment.
            </p>
          </div>
        ) : isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Looking up documents…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <div className="icon">
              <I.ClipboardCheck size={16} />
            </div>
            <h3>Couldn't load documents</h3>
            <p>The request to the go-pim admin API failed. Try again shortly.</p>
          </div>
        ) : documents.length === 0 && isFetched ? (
          <div className="empty">
            <div className="icon">
              <I.ClipboardCheck size={16} />
            </div>
            <h3>No documents</h3>
            <p>
              No generated documents for{" "}
              <span className="mono">{query.payment_ref}</span>.
            </p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Tax</th>
                <th>Issued</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {documents.map((d) => (
                <tr key={d.id}>
                  <td>
                    <div className="cell-name">{d.number || d.id}</div>
                    <div className="cell-id dim">
                      {d.template || "—"}
                      {d.taxation ? ` · ${d.taxation}` : ""}
                    </div>
                  </td>
                  <td>
                    <StatusPill status={d.type} />
                  </td>
                  <td className="mono">
                    {fmtCcyMinor(d.amount_minor, d.currency)}
                  </td>
                  <td className="mono dim">
                    {fmtCcyMinor(d.tax_amount_minor, d.currency)}
                  </td>
                  <td className="dim">
                    {d.issued_at ? formatDateTime(d.issued_at) : "—"}
                  </td>
                  <td className="right">
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {d.url ? (
                        <>
                          <a
                            className="btn ghost sm"
                            href={d.url}
                            target="_blank"
                            rel="noopener"
                          >
                            <I.ArrowUpRight size={11} /> Open
                          </a>
                          <button
                            className="btn ghost sm"
                            type="button"
                            onClick={() => copyLink(d.url)}
                          >
                            <I.Copy size={11} /> Copy link
                          </button>
                        </>
                      ) : (
                        <Pill tone="">no link</Pill>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
